import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  GraduationCap,
  Heart,
  Camera,
  Save,
  Eye,
  EyeOff,
  Calendar,
  Facebook,
  AlertCircle,
  X,
  Upload
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../utils/axios.config';
import { baseUrl } from '../../utils/url';

// Import location data
import upazilaData from '../../data/upazila.json';

function EditProfile() {
  const [isLoading, setIsLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [user, setUser] = useState(null);

  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
    reset,
    setValue,
  } = useForm();

  // Blood group options
  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  
  // Marital status options
  const maritalStatusOptions = ['অবিবাহিত', 'বিবাহিত', 'বিপত্নীক', 'ডিভোর্স'];

  // Role options (for display only, not editable)
  const roleLabels = {
    'adviser': 'উপদেষ্টা',
    'principal-adviser': 'প্রধান উপদেষ্টা', 
    'blood-donor': 'রক্তদাতা',
    'member': 'সদস্য',
    'talent-student': 'মেধাবী শিক্ষার্থী',
    'university-student': 'বিশ্ববিদ্যালয় শিক্ষার্থী',
    'donor': 'দাতা',
    'sponsor': 'স্পন্সর',
    'freelancer': 'ফ্রিল্যান্সার',
    'volunteer': 'স্বেচ্ছাসেবক'
  };

  // Get current user data
  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        return JSON.parse(userData);
      }
      return null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  };

  // Fetch user profile data
  const fetchUserData = async () => {
    try {
      setFetchingData(true);
      const currentUser = getCurrentUser();
      
      if (!currentUser?.user?._id) {
        toast.error('ব্যবহারকারীর তথ্য পাওয়া যায়নি');
        return;
      }

      const { data } = await api.get(`user/profile/${currentUser.user._id}`);
      
      if (data.success) {
        const userData = data.data.user;
        setUser(userData);
        setExistingImageUrl(userData.imageUrl);

        // Populate form with existing data
        reset({
          name: userData.name,
          nameEn: userData.nameEn || '',
          father: userData.father || '',
          mother: userData.mother || '',
          occupation: userData.occupation || '',
          email: userData.email || '',
          mobile: userData.mobile,
          institute: userData.institute,
          designation: userData.designation,
          division: userData.division,
          district: userData.district,
          upazila: userData.upazila,
          union: userData.union,
          facebookUrl: userData.facebookUrl || '',
          bloodGroup: userData.bloodGroup,
          isAgreeDonateBlood: userData.isAgreeDonateBlood,
          lastBloodDonation: userData.lastBloodDonation || '',
          nid: userData.nid,
          dob: userData.dob,
          educationQualification: userData.educationQualification,
          maritalStatus: userData.maritalStatus,
          nnsID: userData.nnsID || '',
        });
      }
    } catch (error) {
      toast.error('ব্যবহারকারীর তথ্য আনতে ব্যর্থ');
      console.error('Failed to fetch user data:', error);
    } finally {
      setFetchingData(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  // Handle image preview
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);

    const formData = new FormData();

    // Append image file if new image is selected
    if (selectedFile) {
      formData.append('image', selectedFile);
    }

    // Append form data
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined && data[key] !== null) {
        formData.append(key, data[key]);
      }
    });

    try {
      const currentUser = getCurrentUser();
      
      if (!currentUser?.user?._id) {
        toast.error('ব্যবহারকারীর তথ্য পাওয়া যায়নি');
        return;
      }

      const { data: responseData } = await api.put(`user/profile/${currentUser.user._id}`, formData);

      if (responseData.success) {
        toast.success('প্রোফাইল সফলভাবে আপডেট হয়েছে');
        
        // Update localStorage with new user data
        const updatedUserData = {
          ...currentUser,
          user: {
            ...currentUser.user,
            ...responseData.data.user
          }
        };
        localStorage.setItem('user', JSON.stringify(updatedUserData));
        
        // Refresh the data
        fetchUserData();
      } else {
        toast.error(responseData.error || 'আপডেট করতে ব্যর্থ');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'একটি ত্রুটি ঘটেছে');
    } finally {
      setIsLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">ব্যবহারকারীর তথ্য পাওয়া যায়নি</h3>
        <p className="text-gray-600 mb-6">আপনার প্রোফাইল ডেটা লোড করতে সমস্যা হয়েছে।</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">প্রোফাইল সম্পাদনা</h1>
          <p className="text-gray-600 mt-1">
            আপনার ব্যক্তিগত তথ্য আপডেট করুন
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Profile Image Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Camera className="w-5 h-5 mr-2 text-primary" />
            প্রোফাইল ছবি
          </h2>
          
          <div className="flex items-center space-x-6">
            <div className="relative">
              <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-gray-200">
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : existingImageUrl ? (
                  <img src={`${baseUrl}${existingImageUrl}`} alt="Current" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <User size={48} className="text-gray-400" />
                  </div>
                )}
              </div>
              {imagePreview && (
                <button
                  type="button"
                  onClick={() => {
                    setImagePreview(null);
                    setSelectedFile(null);
                  }}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            
            <div>
              <label 
                htmlFor="profile-image"
                className="cursor-pointer bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-200"
              >
                <Upload size={20} />
                নতুন ছবি আপলোড করুন
              </label>
              <input
                id="profile-image"
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleImageChange}
              />
              <p className="text-xs text-gray-500 mt-2">PNG, JPG বা GIF (সর্বোচ্চ 5MB)</p>
            </div>
          </div>
        </div>

        {/* Basic Information Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <User className="w-5 h-5 mr-2 text-primary" />
            ব্যক্তিগত তথ্য
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                নাম *
              </label>
              <input
                {...register('name', {
                  required: 'নাম আবশ্যক',
                  minLength: { value: 2, message: 'নাম কমপক্ষে ২ অক্ষরের হতে হবে' },
                  maxLength: { value: 100, message: 'নাম সর্বোচ্চ ১০০ অক্ষরের হতে পারে' }
                })}
                type="text"
                placeholder="আপনার পূর্ণ নাম লিখুন"
                className="input input-bordered w-full"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Name in English */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                নাম (ইংরেজিতে)
              </label>
              <input
                {...register('nameEn', {
                  maxLength: { value: 100, message: 'ইংরেজি নাম সর্বোচ্চ ১০০ অক্ষরের হতে পারে' }
                })}
                type="text"
                placeholder="Your name in English"
                className="input input-bordered w-full"
              />
              {errors.nameEn && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.nameEn.message}
                </p>
              )}
            </div>

            {/* Father's Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                পিতার নাম
              </label>
              <input
                {...register('father', {
                  maxLength: { value: 100, message: 'পিতার নাম সর্বোচ্চ ১০০ অক্ষরের হতে পারে' }
                })}
                type="text"
                placeholder="আপনার পিতার নাম"
                className="input input-bordered w-full"
              />
              {errors.father && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.father.message}
                </p>
              )}
            </div>

            {/* Mother's Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                মাতার নাম
              </label>
              <input
                {...register('mother', {
                  maxLength: { value: 100, message: 'মাতার নাম সর্বোচ্চ ১০০ অক্ষরের হতে পারে' }
                })}
                type="text"
                placeholder="আপনার মাতার নাম"
                className="input input-bordered w-full"
              />
              {errors.mother && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.mother.message}
                </p>
              )}
            </div>

            {/* Occupation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                পেশা
              </label>
              <input
                {...register('occupation', {
                  maxLength: { value: 100, message: 'পেশা সর্বোচ্চ ১০০ অক্ষরের হতে পারে' }
                })}
                type="text"
                placeholder="আপনার পেশা"
                className="input input-bordered w-full"
              />
              {errors.occupation && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.occupation.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ইমেইল
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  {...register('email', {
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'সঠিক ইমেইল ঠিকানা লিখুন'
                    }
                  })}
                  type="email"
                  placeholder="আপনার ইমেইল ঠিকানা"
                  className="input input-bordered w-full pl-10"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Mobile */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                মোবাইল নম্বর *
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  {...register('mobile', {
                    required: 'মোবাইল নম্বর আবশ্যক',
                    pattern: {
                      value: /^(\+88)?01[3-9]\d{8}$/,
                      message: 'সঠিক মোবাইল নম্বর লিখুন'
                    }
                  })}
                  type="tel"
                  placeholder="01XXXXXXXXX"
                  className="input input-bordered w-full pl-10"
                />
              </div>
              {errors.mobile && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.mobile.message}
                </p>
              )}
            </div>

            {/* NID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                জাতীয় পরিচয়পত্র নং *
              </label>
              <input
                {...register('nid', {
                  required: 'জাতীয় পরিচয়পত্র নং আবশ্যক'
                })}
                type="text"
                placeholder="জাতীয় পরিচয়পত্র নং"
                className="input input-bordered w-full"
              />
              {errors.nid && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.nid.message}
                </p>
              )}
            </div>

            {/* NNS ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                এনএনএস আইডি
              </label>
              <input
                {...register('nnsID')}
                type="text"
                placeholder="এনএনএস আইডি (যদি থাকে)"
                className="input input-bordered w-full"
              />
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                জন্ম তারিখ *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  {...register('dob', {
                    required: 'জন্ম তারিখ আবশ্যক'
                  })}
                  type="text"
                  placeholder="DD-MM-YYYY"
                  className="input input-bordered w-full pl-10"
                />
              </div>
              {errors.dob && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.dob.message}
                </p>
              )}
            </div>

            {/* Marital Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                বৈবাহিক অবস্থা *
              </label>
              <select
                {...register('maritalStatus', { required: 'বৈবাহিক অবস্থা নির্বাচন আবশ্যক' })}
                className="select select-bordered w-full"
              >
                <option value="">নির্বাচন করুন</option>
                {maritalStatusOptions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              {errors.maritalStatus && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.maritalStatus.message}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Professional Information Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Briefcase className="w-5 h-5 mr-2 text-primary" />
            পেশাগত তথ্য
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Institute */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                প্রতিষ্ঠান *
              </label>
              <input
                {...register('institute', { required: 'প্রতিষ্ঠানের নাম আবশ্যক' })}
                type="text"
                placeholder="প্রতিষ্ঠানের নাম"
                className="input input-bordered w-full"
              />
              {errors.institute && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.institute.message}
                </p>
              )}
            </div>

            {/* Designation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                পদবি *
              </label>
              <input
                {...register('designation', { required: 'পদবি আবশ্যক' })}
                type="text"
                placeholder="আপনার পদবি"
                className="input input-bordered w-full"
              />
              {errors.designation && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.designation.message}
                </p>
              )}
            </div>

            {/* Education Qualification */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                শিক্ষাগত যোগ্যতা *
              </label>
              <div className="relative">
                <GraduationCap className="absolute left-3 top-3 text-gray-400" size={20} />
                <textarea
                  {...register('educationQualification', { required: 'শিক্ষাগত যোগ্যতা আবশ্যক' })}
                  rows={3}
                  placeholder="আপনার শিক্ষাগত যোগ্যতার বিস্তারিত"
                  className="textarea textarea-bordered w-full pl-10"
                />
              </div>
              {errors.educationQualification && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.educationQualification.message}
                </p>
              )}
            </div>

            {/* Role (Read-only) */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ভূমিকা
              </label>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="text-gray-800 font-medium">
                  {roleLabels[user.role] || user.role}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Address Information Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <MapPin className="w-5 h-5 mr-2 text-primary" />
            ঠিকানা
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Division */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                বিভাগ *
              </label>
              <input
                {...register('division', { required: 'বিভাগ আবশ্যক' })}
                type="text"
                placeholder="বিভাগ"
                className="input input-bordered w-full"
              />
              {errors.division && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.division.message}
                </p>
              )}
            </div>

            {/* District */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                জেলা *
              </label>
              <input
                {...register('district', { required: 'জেলা আবশ্যক' })}
                type="text"
                placeholder="জেলা"
                className="input input-bordered w-full"
              />
              {errors.district && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.district.message}
                </p>
              )}
            </div>

            {/* Upazila */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                উপজেলা *
              </label>
              <input
                {...register('upazila', { required: 'উপজেলা আবশ্যক' })}
                type="text"
                placeholder="উপজেলা"
                className="input input-bordered w-full"
              />
              {errors.upazila && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.upazila.message}
                </p>
              )}
            </div>

            {/* Union */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ইউনিয়ন *
              </label>
              <input
                {...register('union', { required: 'ইউনিয়ন আবশ্যক' })}
                type="text"
                placeholder="ইউনিয়ন"
                className="input input-bordered w-full"
              />
              {errors.union && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.union.message}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Blood Information Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Heart className="w-5 h-5 mr-2 text-primary" />
            রক্তদান সংক্রান্ত তথ্য
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Blood Group */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                রক্তের গ্রুপ *
              </label>
              <select
                {...register('bloodGroup', { required: 'রক্তের গ্রুপ নির্বাচন আবশ্যক' })}
                className="select select-bordered w-full"
              >
                <option value="">নির্বাচন করুন</option>
                {bloodGroups.map(group => (
                  <option key={group} value={group.toLowerCase()}>{group}</option>
                ))}
              </select>
              {errors.bloodGroup && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.bloodGroup.message}
                </p>
              )}
            </div>

            {/* Last Blood Donation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                সর্বশেষ রক্তদানের তারিখ
              </label>
              <input
                {...register('lastBloodDonation')}
                type="text"
                placeholder="DD-MM-YYYY"
                className="input input-bordered w-full"
              />
            </div>

            {/* Agree to Donate Blood */}
            <div className="lg:col-span-2">
              <div className="form-control">
                <label className="label cursor-pointer justify-start">
                  <input
                    {...register('isAgreeDonateBlood')}
                    type="checkbox"
                    className="checkbox checkbox-primary mr-3"
                  />
                  <span className="label-text">আমি রক্তদানে সম্মত</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Social Information Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Facebook className="w-5 h-5 mr-2 text-primary" />
            সামাজিক মাধ্যম
          </h2>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ফেসবুক প্রোফাইল লিঙ্ক
            </label>
            <div className="relative">
              <Facebook className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                {...register('facebookUrl')}
                type="url"
                placeholder="https://facebook.com/yourprofile"
                className="input input-bordered w-full pl-10"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => fetchUserData()}
            className="btn btn-outline"
            disabled={isLoading}
          >
            পুনরায় লোড করুন
          </button>
          
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary min-w-[150px]"
          >
            {isLoading ? (
              <>
                <span className="loading loading-spinner loading-sm mr-2"></span>
                আপডেট করা হচ্ছে...
              </>
            ) : (
              <>
                <Save size={20} className="mr-2" />
                প্রোফাইল আপডেট করুন
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditProfile;
